import * as React from "react";
import { Metric } from "../../../components/general/custom";
import RangeSelector from "../../../components/range_selector/range_selector";
import { Card } from "../../../components/general/card";
import { Box } from "@mui/material";
import Metering from "../../../components/metering_tab/metering";
import { useParams } from "react-router-dom";
import useBackend, { useLogin } from "../../../hooks/use_backend";
import { useQuery } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../../enums/react_query_key_enum";
import { fetchSiteData, fetchSiteProduct } from "../../../scripts/api_v2/site_client";
import {
  fetchMeteringActuals,
  fetchMeteringForecast,
  fetchMeteringHistorical,
  fetchImbalancePriceEpex,
  fetchMeteringPeak,
} from "../../../scripts/api_v2/metering_client";
import { startOfDay, endOfDay, differenceInCalendarDays, startOfMonth, endOfMonth, subYears } from "date-fns";

export default function MeteringTab({ required_privilege }) {
  const params = useParams();
  const backend = useBackend();
  const today = new Date();
  const [range, setRange] = React.useState({
    startDate: startOfDay(today),
    endDate: endOfDay(today),
    key: "selection",
  });

  const handleRangeChange = (newRange) => {
    setRange(newRange);
  };

  const baseDate = range.startDate;
  const daysAfter = differenceInCalendarDays(range.endDate, range.startDate);
  const isViewingToday = endOfDay(today).getTime() === endOfDay(range.endDate).getTime();

  // Keep peak chart period aligned with Financial tab: same month one year ago -> selected end month.
  const peakFromts = Math.round(startOfMonth(subYears(range.endDate, 1)).getTime() / 1000);
  const peakTots = Math.round(endOfMonth(range.endDate).getTime() / 1000);

  let { data: siteData } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
    queryFn: () => fetchSiteData(backend.fetch, params.site_id),
    enabled: false, // sitePage will refresh the query
  });

  let { data: meteringActuals, isLoading: fetchingActuals } = useQuery({
    queryKey: [REACT_QUERY_KEYS.METERING_ACTUALS, backend.fetch, params.site_id, range],
    queryFn: () => fetchMeteringActuals(backend.fetch, params.site_id, baseDate, 0, daysAfter),
    refetchInterval: isViewingToday ? 30000 : false,
  });

  let { data: meteringHistorical, isLoading: fetchingMetering } = useQuery({
    queryKey: [REACT_QUERY_KEYS.METERING_HISTORICAL, backend.fetch, params.site_id, range],
    queryFn: () => fetchMeteringHistorical(backend.fetch, params.site_id, baseDate, 0, daysAfter),
    refetchInterval: isViewingToday ? 40000 : false,
  });

  let { data: imbalancePriceEpex, isLoading: fetchingPrices } = useQuery({
    queryKey: [REACT_QUERY_KEYS.IMBALANCE_EPEX, backend.fetch, range],
    queryFn: () => fetchImbalancePriceEpex(backend.fetch, baseDate, 0, daysAfter),
    refetchInterval: isViewingToday ? 900000 : false,
  });

  let { data: product } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_PRODUCT, backend.fetch, params.site_id],
    queryFn: () => fetchSiteProduct(backend.fetch, params.site_id),
  });

  const gridReferenceLines = {
      maxGridOfftakeSetpoint: siteData?.config?.max_grid_offtake_sp
  };

  let { data: peakData, isLoading: fetchingPeak } = useQuery({
    queryKey: [REACT_QUERY_KEYS.METERING_PEAK, backend.fetch, params.site_id, peakFromts, peakTots],
    queryFn: () => fetchMeteringPeak(backend.fetch, params.site_id, peakFromts, peakTots),
    refetchInterval: isViewingToday ? 60000 : false,
  });

  // Always fetch forecast on mount (no `enabled: false`) so non-EPEX sites aren't
  // blocked waiting for the product query. Once we know conf_optimizer is EPEX,
  // disable refetching since the data won't be available.
  const isEpexSteering = product?.conf_optimizer === "EPEX";
  let { data: meteringForecast, isLoading: fetchingForecast } = useQuery({
    queryKey: [REACT_QUERY_KEYS.METERING_FORECAST, backend.fetch, params.site_id, range],
    queryFn: () => fetchMeteringForecast(backend.fetch, params.site_id, baseDate, 0, daysAfter),
    refetchInterval: isViewingToday && !isEpexSteering ? 60000 : false,
  });

  if (!params.site_id) return;

  return (
    <>
      <HeaderCard siteData={siteData} metering={meteringHistorical?.metering} profits={meteringHistorical?.profits} range={range} setRange={handleRangeChange} />
      <Metering
        metering={meteringHistorical?.metering ?? []}
        profits={meteringHistorical?.profits ?? []}
        actuals={meteringActuals ?? []}
        forecast={meteringForecast ?? []}
        prices={imbalancePriceEpex?.imbalanceprice_epex ?? []}
        gridReferenceLines={gridReferenceLines ?? {}}
        fetchingActuals={fetchingActuals}
        fetchingMetering={fetchingMetering}
        fetchingForecast={fetchingForecast}
        fetchingPrices={fetchingPrices}
        Optimizer={product?.conf_optimizer} // Using conf_optimizer instead of act_optimizer because actual can be 'disabled' or 'active_steering' while forecast is still 'EPEX'
        peakData={peakData ?? []}
        fetchingPeak={fetchingPeak}
        multiDay={daysAfter > 0}
      />
    </>
  );
}

const totalCharge = (data) => {
  if (data.flex_charge && data.flex_discharge) return data.flex_charge + data.flex_discharge;
  else if (data.flex_charge && !data.flex_discharge) return data.flex_charge;
  else if (data.flex_discharge && !data.flex_charge) return data.flex_discharge;
  else return 0;
};

const calcCharge = (data) => {
  if (data === undefined || data === null) return null;
  return data.reduce((v, el) => v + (el.flex_charge ? el.flex_charge / 12000 : 0), 0);
};

const calcDisharge = (data) => {
  if (data === undefined || data === null) return null;
  return data.reduce((v, el) => v + (el.flex_discharge ? el.flex_discharge / 12000 : 0), 0);
};

const calcCycles = (data, capacity) => {
  if (data === undefined || data === null || !capacity) return null;

  return (
    data.reduce((v, el) => {
      return v + totalCharge(el);
    }, 0) /
    (12 * 2 * capacity)
  );
};

function HeaderCard({ siteData, metering, range, setRange }) {
  const totalCharge = React.useMemo(() => calcCharge(metering), [metering]);
  const totalDischarge = React.useMemo(() => calcDisharge(metering), [metering]);
  const cycles = React.useMemo(() => calcCycles(metering, siteData?.capacity), [metering, siteData]); //divided by twelve because of 5 min sampling
  return (
    <Card display="flex" flexDirection="column" justifyContent="center" alignItems="center" my={2}>
      <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">
        <RangeSelector range={range} setRange={setRange} />
      </Box>
      <Box display="flex" flexDirection="row" alignItems="center">
        {/* <Metric name="Profit" value={totalProfit !== null ? "€" + Math.round(100 * totalProfit) / 100 : "-"} /> */}
        <Metric name="Charged" value={totalCharge !== null ? Math.round(totalCharge) + " kWh" : "-"} />
        <Metric name="Discharged" value={totalDischarge !== null ? Math.round(totalDischarge) + " kWh" : "-"} />
        <Metric
          name="Cycles"
          value={cycles !== null ? Math.round(100 * cycles) / 100 : "-"}
          info="A cycle is a full charge and discharge of the battery. Going from 0% to 100% and back to 0% counts as 1 cycle."
        />
      </Box>
    </Card>
  );
}
